/**
 * Provides constants for lender gear
 *
 * @prettier
 * @author Alexander Nagelberg
 */

export const AUTHORIZATIONS = {
  REMITTANCE: 'Can remit loans for guaranty for own lender'
}

export const API_URLs = {
  //API_BASE_PATH: '/lgyhub/guaranty-remittance/api/loans', // TODO: uncomment this and remove bellow when integrated
  API_BASE_PATH:
    window.localStorage.GRM_PATH != null ? window.localStorage.GRM_PATH : '/lgyhub/api/guaranty-remittance',
  SEARCH_PATH: 'search/:lin',
  ADD_OBLIGOR_PATH: ':lin/obligors/add',
  EDIT_OBLIGOR_PATH: ':lin/obligors/:obligor/edit',
  EDIT_LOAN_INFORMATION_PATH: ':lin/loan',
  EDIT_UNDERWRITER_INFO_PATH: ':lin/underwriting',
  EDIT_TITLE_NON_OBLIGOR_PATH: ':lin/title',
  SUBMIT_FUNDING_FEE_REVIEW_PATH: ':lin/funding-fee/review',
  SUBMIT_GUARANTY_FUNDING_FEE_PATH: ':lin/final-review',
  SUBMIT_DISPOSITION_PATH: ':lin/priorvaloan/:priorVALoan/disposition',
  DELETE_REMITTANCE: ':lin',
  RECALCULATE_GUARANTY_PATH: ':lin/guaranty/recalculate'
}

export const GEAR_URLs = {
  GEAR_BASE_PATH: '/lgyhub/2/lender',
  SEARCH_PATH: 'search',
  REMITTANCE_WIZARD_PATH: 'remittance/:lin',
  OBLIGORS_PATH: 'obligors',
  EDIT_OBLIGORS_PATH: 'obligors/:obligor/edit',
  ADD_OBLIGORS_PATH: 'obligors/add',
  FUNDING_FEE_REVIEW_PATH: 'fundingfee',
  LOAN_INFORMATION_PATH: 'information',
  UNDERWRITING_INFO_PATH: 'underwriting',
  GUARANTY_REVIEW_AND_SUBMIT_PATH: 'confirm',
  DISPOSITION_LOAN_PATH: 'confirm/:priorVALoan/disposition',
  UPLOAD_DOCUMENTS_PATH: 'confirm/:priorVALoan/upload',
  LGC_STATUS_PATH: 'lgc-status'
}

export const ENCODINGS = {
  obligorType: {
    VETERAN_USING_ENTITLEMENT: 'Veteran Using Entitlement',
    SPOUSE_OF_VETERAN_USING_ENTITLEMENT: 'Spouse of Veteran Using Entitlement',
    NON_VETERAN_VETERAN_NOT_USING_ENTITLEMENT: 'Non-Veteran / Veteran Not Using Entitlement',
    SURVIVING_SPOUSE_USING_DECEASED_VETERAN_ENTITLEMENT: 'Surviving Spouse Using Deceased Veteran Entitlement',
    WIDOW_ON_IRRRL: 'Widow on IRRRL (Interest Rate Reduction Refinance Loan)'
  },
  gender: {
    male: 'Male',
    female: 'Female',
    doesNotWishToProvide: 'I do not wish to provide this information'
  },
  ethnicity: {
    mexican: 'Mexican',
    puertoRican: 'Puerto Rican',
    cuban: 'Cuban',
    nonHispanicOrLatino: 'Non-Hispanic or Latino',
    doesNotWishToProvide: 'I do not wish to provide this information',
    hispanicOrLatino: 'Hispanic or Latino',
    otherHispanicOrLatino: 'Other Hispanic or Latino',
    otherHispanicOrLatinoOrigin: 'Enter Origin'
  },
  race: {
    americanIndianAlaskanNative: 'American Indian or Alaska Native',
    americanIndianAlaskanNativeTribe: 'Enter name of enrolled or principal tribe',
    asian: 'Asian',
    asianIndian: 'Asian Indian',
    chinese: 'Chinese',
    filipino: 'Filipino',
    japanese: 'Japanese',
    korean: 'Korean',
    vietnamese: 'Vietnamese',
    otherAsian: 'Other Asian',
    otherAsianRace: 'Enter Race',
    blackOrAfricanAmerican: 'Black or African American',
    nativeHawaiian: 'Native Hawaiian',
    nativeHawaiianOrOtherPacificIslander: 'Native Hawaiian or Other Pacific Islander',
    guamanianOrChamorro: 'Guamanian or Chamorro',
    samoan: 'Samoan',
    otherPacificIslander: 'Other Pacific Islander',
    otherPacificIslanderRace: 'Enter Race',
    white: 'White',
    doesNotWishToProvide: 'I do not wish to provide this information'
  },
  eligibilityType: {
    WWII: 'World War II',
    POST_WWII: 'Post-World War II',
    KOREAN: 'Korean War',
    POST_KOREAN: 'Post-Korean War',
    VIETNAM: 'Vietnam War',
    POST_VIETNAM: 'Post-Vietnam War',
    PERSIAN_GULF: 'Persian Gulf War',
    RESERVE_NATIONAL_GUARD: 'Reserve / National Guard',
    SURVIVING_SPOUSE: 'Surviving Spouse',
    SPOUSE_POW_OR_MIA: 'Spouse of POW (Prisoner of War) / MIA (Missing in Action)'
  },
  demographicProvidedBy: {
    FACE_TO_FACE: 'Face-to-Face Interview (Includes Electronic Media W/ Video Component)',
    TELEPHONE: 'Telephone Interview',
    FAX_OR_MAIL: 'Fax or Mail',
    EMAIL_OR_INTERNET: 'E-mail or Internet'
  },
  mannerHowTitleHeld: {
    SOLE_OWNERSHIP: 'Sole Ownership',
    TENANCY_IN_COMMON: 'Tenancy in Common',
    JOINT_TENANCY_WITH_RIGHT_OF_SURVIVORSHIP: 'Joint Tenancy with Right of Survivorship',
    TENANCY_BY_ENTIRETY: 'Tenancy by the Entirety'
  },
  relationshipToObligors: {
    SPOUSE_OF_VETERAN: 'Spouse of Veteran Using Entitlement',
    NOT_SPOUSE_OF_VETERAN: 'Not a Spouse of Veteran Using Entitlement'
  },
  loanCategory: {
    PURCHASE: 'Purchase',
    IRRRL: 'IRRRL (Interest Rate Reduction Refinance Loan)',
    CASHOUT_REFINANCE: 'Cashout Refinance',
    CONSTRUCTION: 'Construction',
    ASSUMPTION: 'Assumption'
  },
  amortizationType: {
    FIXED_RATE: 'Fixed Rate',
    ARM: 'Adjustable Rate Mortgage(ARM)'
  },
  underwritingMethod: {
    AUS_DESKTOP_UNDERWRITER: 'AUS/Desktop Underwriter',
    AUS_DESKTOP_PROSPECTOR: 'AUS/Loan Prospector',
    MANUAL: 'Manual'
  },
  riskClassification: {
    APPROVE: 'Approve',
    REFER: 'Refer'
  },
  lastSavedScreen: {
    // maps LastSavedScreen enums to existing GEAR_URLs
    LENDER_GEAR_SEARCH: GEAR_URLs.SEARCH_PATH,
    MORE_RECENT_LIN: '', // TODO
    MORE_RECENT_NOV: '', // TODO
    OBLIGORS_AND_VETERANS: GEAR_URLs.OBLIGORS_PATH,
    CLARIFY_PROPERTY_DATA: '', // TODO
    CONFIRM_PURCHASE: '', // TODO
    SIMILAR_PROP: '', // TODO
    CASHOUT_PRIOR_LOAN: '', // TODO
    ENTER_LOAN_INFORMATION: GEAR_URLs.LOAN_INFORMATION_PATH,
    UNDERWRITING_INFORMATION: GEAR_URLs.UNDERWRITING_INFO_PATH,
    SEASONING_REQUIREMENTS: '', // TODO
    FEE_RECOUPMENT: '', // TODO
    NTB_3709: '', // TODO
    CASHOUT_REQUIREMENTS: '', // TODO
    IRRRL_REQUIREMENTS: '', // TODO
    REVIEW_FUNDING_FEE: GEAR_URLs.FUNDING_FEE_REVIEW_PATH,
    REVIEW_GUARANTY_AND_SUBMIT: GEAR_URLs.GUARANTY_REVIEW_AND_SUBMIT_PATH,
    REVIEW_REMITTED_LOAN: GEAR_URLs.LGC_STATUS_PATH
  },
  serviceType: {
    ACTIVE_DUTY: 'Active Duty',
    RESERVE_NATIONAL_GUARD_DUTY: 'Reserve/National Guard Duty'
  },
  firstOrSubsequentUseOfBenefit: {
    FIRST_USE: 'First Use',
    SUBSEQUENT_USE: 'Subsequent Use'
  },
  fundingFeeExempt: {
    NOT_EXEMPT: 'No',
    EXEMPT: 'Yes'
  },
  lgcStatus: {
    AVAILABLE: 'Available',
    PENDING: 'Pending',
    ERROR: 'Error',
    RESCINDED_LOAN: 'Rescinded Loan'
  },
  collectionStatus: {
    REMITTED_TO_VA_PENDING_COLLECTION_WITH_TREASURY: 'Remitted to VA pending collection with Treasury',
    COLLECTED_BY_TREASURY: 'Collected by Treasury',
    TREASURY_COULDNT_COLLECT_INSUFFICIENT_FUNDINGS: 'Treasury couldn’t collect, insufficient funds',
    TREASURY_COULDNT_COLLECT_LENDER_ACCOUNT_ISSUE: 'Treasury couldn’t collect, lender account incorrect',
    NO_REQUIRED_FUNDING_FEE: 'No required Funding Fee'
  },
  encumbranceReason: {
    ACTIVE_LOAN: 'Active Loan',
    TERMINATED_UNRESTORED_LOAN: 'Terminated Unrestored Loan',
    UNRECOVERED_CLAIM_AGAINST_GUARANTY: 'Unrecovered Claim Against Guaranty'
  },
  loanRelationship: {
    OBLIGOR: 'Obligor who is not the spouse of an Entitling Veteran',
    NON_OBLIGOR: 'Non-Obligor on title who is not the spouse of an Entitling Veteran'
  },
  lenderIndicatedVeteranOwnership: {
    VETERAN_STILL_OWNS_THIS_PROPERTY: 'The Veteran still owns this property',
    VETERANS_DOES_NOT_OWN_THIS_PROPERTY: 'The Veteran does not own this property'
  },
  lenderCertifiedSoldAndPaidInFull:
    'I certify that the Veteran no longer owns this property and the VA loan on it has been paid in full.',
  lenderCertifiedNoLongerOwned: 'I certify that the Veteran no longer owns this property.',
  lenderCertifiedUseOneTimeRestoration:
    'I certify that the Veteran wants to use their one-time restoration on this loan/property.'
}

export const fullURL = (base, path, param, pathVariables) => {
  let adjustedPath = `${base}/${path}` + (param ? `/${param}` : '')
  if (pathVariables != null) {
    Object.keys(pathVariables).forEach(key => {
      adjustedPath = adjustedPath.split(`:${key}`).join(pathVariables[key])
    })
  }
  return adjustedPath
}

/**
 * Creates full URL path for API endpoints. Takes in an action endpoint
 * and spits out the full path.
 *
 * @param {String} path URL path relative to the base URL
 * @param {String} [param] (optional) additional URL path appended to path
 * @param {Object} pathVariables (optional) set path variables (e.g. passing
 *  in { id: 123 } to a path '/loan/:id' will return '/loan/123')
 * @returns joined URLs
 */
export const fullAPIURL = (path, param, pathVariables) => fullURL(API_URLs.API_BASE_PATH, path, param, pathVariables)

/**
 * Creates full URL path for gear endpoints. Takes in an action endpoint
 * and spits out the full path.
 *
 * @param {String} path URL path relative to the base URL
 * @param {String} param (optional) additional URL path appended to path
 * @param {Object} pathVariables (optional) set path variables (e.g. passing
 *  in { id: 123 } to a path '/loan/:id' will return '/loan/123')
 * @returns joined URLs
 */
export const fullGearURL = (path, param, pathVariables) => fullURL(GEAR_URLs.GEAR_BASE_PATH, path, param, pathVariables)

/**
 * Generate a full name from first, middle, last
 *
 * @param {Object} props
 * @param {String} props.firstName
 * @param {String} [props.middle] Middle initial or name
 * @param {String} props.lastName
 * @returns First [middle ]Last
 */
export const fullName = ({ firstName, middleName, lastName }) =>
  middleName ? [firstName, middleName, lastName].join(' ') : [firstName, lastName].join(' ')
